import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
// @ts-ignore - Remotion型定義の互換性問題の暫定対応（既存remotionコンポジション踏襲）
import { Player, PlayerRef } from '@remotion/player';
import {
  CaptionSegment,
  CaptionStyle,
  SongAdProject,
  SongAdScene,
  SongAdTimeline,
  TelopPosition,
  TelopSize,
} from '../../services/api/songAdService';
import { TaskyButton, TaskyTextarea } from '../tasky';
import {
  SongAdComposition,
  deriveCompositionScenes,
  deriveCtaCard,
  getSongAdDimensions,
  getSongAdDurationInFrames,
  SONG_AD_FPS,
  CAPTION_STYLE_OPTIONS,
  CAPTION_POSITION_OPTIONS,
  CAPTION_SIZE_OPTIONS,
  stabilizeCompositionMedia,
} from '../../remotion/song-ad';
import {
  captionHistoryReducer,
  createCaptionAt,
  initCaptionHistory,
  moveCaption,
  resizeCaptionEnd,
  resizeCaptionStart,
  splitCaptionAt,
} from './timelineEditor';
import {
  ArrowLeft,
  Download,
  Loader2,
  Lock,
  Music,
  Pause,
  Play,
  Plus,
  Redo2,
  Scan,
  Scissors,
  Subtitles,
  Trash2,
  Undo2,
  Video,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';

/**
 * 詳細編集（タイムラインエディタ、spec §14〜§16）。
 *
 * timeline.captions（テロップ配列）だけをこの画面で編集する。シーンの並び替え・モーション設定は
 * 既存の③ストーリーボード/④モーションで完結しており重複させない（意図的な範囲限定）。
 * timeline JSON自体に持たないシーン境界時刻は sceneTiming（deriveCompositionScenes）で
 * 都度導出するため、映像トラックは「選択してシークできる参照表示」のみで境界ドラッグは行わない
 * （mockup.htmlの screen-editor は境界ドラッグを提供するが、backendのタイムラインJSONに
 * シーン毎の開始終了を保持しない設計のため実現不可。GOAL.md v3アーキ決定に基づく意図的差分）。
 *
 * Undo/Redo・分割・ドラッグ移動/伸縮のクランプは timelineEditor.ts の純関数に寄せてある。
 * 自動保存は2秒debounce。保存中/保存済み/エラーをツールバーに表示する。
 */

interface EditorStepProps {
  project: SongAdProject;
  scenes: SongAdScene[];
  onSaveTimeline: (timeline: SongAdTimeline) => Promise<void>;
  onBackToPreview: () => void;
  onProceedToExport: () => void;
}

type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

const AUTOSAVE_DEBOUNCE_MS = 2000;
/**
 * 再生ヘッド（タイムラインの縦線と時刻表示）を更新する最小間隔（秒）。
 * frameupdate は毎秒 SONG_AD_FPS 回飛んでくるが、そのたびに再レンダリングすると
 * メインスレッドが詰まって音声がずれる（v8.1）。0.1秒＝毎秒10回まで間引く。
 */
const PLAYHEAD_UPDATE_SEC = 0.1;
const TEXT_COMMIT_DEBOUNCE_MS = 600;
const MIN_PX_PER_SEC = 6;
const MAX_PX_PER_SEC = 140;

const CAPTION_BLOCK_CLASS: Record<CaptionStyle, string> = {
  simple: 'bg-indigo-50 border-indigo-300 text-indigo-700',
  pop: 'bg-pink-50 border-pink-300 text-pink-700',
  impact: 'bg-amber-50 border-amber-400 text-amber-800',
  premium: 'bg-slate-100 border-slate-400 text-slate-700',
};

const buildDefaultTimeline = (scenes: SongAdScene[]): SongAdTimeline => ({
  captions: [],
  scene_order: [...scenes].sort((a, b) => a.display_order - b.display_order).map((s) => s.id),
  motion: Object.fromEntries(scenes.map((s) => [s.id, { mode: s.motion_mode, prompt: s.motion_prompt }])),
  updated_client_at: '',
});

const formatSec = (sec: number): string => {
  const s = Math.max(0, sec);
  const m = Math.floor(s / 60);
  const rem = (s % 60).toFixed(1).padStart(4, '0');
  return `${m}:${rem}`;
};

const EditorStep: React.FC<EditorStepProps> = ({ project, scenes, onSaveTimeline, onBackToPreview, onProceedToExport }) => {
  const baseTimeline = useMemo(() => project.timeline ?? buildDefaultTimeline(scenes), [project, scenes]);
  const totalDuration = project.song_duration_sec ?? project.target_seconds;

  // Undo/Redoはテロップ配列だけを対象にする（シーン順・モーションはこの画面で編集しない）
  const [history, dispatchHistory] = useReducer(captionHistoryReducer, baseTimeline.captions, initCaptionHistory);
  // ドラッグ中/連続入力中の即時プレビュー（履歴には積まない）。nullなら history.present を表示
  const [liveCaptions, setLiveCaptions] = useState<CaptionSegment[] | null>(null);
  const captions = liveCaptions ?? history.present;

  const [selectedCaptionId, setSelectedCaptionId] = useState<string | null>(null);
  const [pxPerSec, setPxPerSec] = useState(24);
  const [currentSec, setCurrentSec] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

  const playerRef = useRef<PlayerRef | null>(null);
  // Playerはkey変更（durationInFrames等の変更）で再マウントされるRemotionの仕様上、
  // イベント購読をuseEffect(deps: [])で一度だけ行うと再マウント後に新しいPlayerインスタンスへ
  // リスナーが追従せず「旧インスタンスに残る」バグになる（v4原因分析④）。コールバックrefで
  // インスタンスの着脱を検知し、購読effectのdepsに含めて追従させる。
  const [playerVersion, setPlayerVersion] = useState(0);
  const attachPlayerRef = useCallback((instance: PlayerRef | null) => {
    playerRef.current = instance;
    setPlayerVersion((v) => v + 1);
  }, []);
  const timelineScrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textCommitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDirtyRef = useRef(false);

  const compositionScenes = useMemo(
    () => deriveCompositionScenes(scenes, { ...baseTimeline, captions }, totalDuration),
    [scenes, baseTimeline, captions, totalDuration]
  );
  // v4 F9: CTAカード分だけ末尾シーンを縮める（総尺は変えない。タイムラインの映像トラックも
  // プレビューPlayerもこのtrim済みの枠を使い、書き出しと一致させる）
  const { scenes: renderScenes, cta } = useMemo(
    () => deriveCtaCard(compositionScenes, project, totalDuration),
    [compositionScenes, project, totalDuration]
  );
  const dimensions = getSongAdDimensions(project.aspect_ratio);
  const durationInFrames = getSongAdDurationInFrames(totalDuration);
  // v5 k: 自動保存のたびに project が差し替わり、presigned URLが新しい署名で再発行されて
  // <Audio>/<Video>/<Img> がリロードされ再生位置が先頭へ戻る問題への対策。
  // S3オブジェクトパス基準でURL文字列値を安定させる（advisor-notes.md (2)）
  const stableMedia = useMemo(
    () => stabilizeCompositionMedia(renderScenes, cta, project.song_url),
    [renderScenes, cta, project.song_url]
  );

  /* ---------- Player <-> 再生ヘッド同期 ---------- */

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return undefined;
    // v8.1: frameupdate は再生中 fps 回（=毎秒30回）飛んでくる。これをそのまま setState すると
    // 3ペイン＋タイムライン全体が毎フレーム再レンダリングされ、メインスレッドが詰まって
    // Remotion Player のクロック（requestAnimationFrame）が遅延する。一方 <Audio> は実時間で
    // 進み続けるため音声が先行し、Remotion が acceptableTimeShiftInSeconds（既定0.45秒）を
    // 超えたと判断して currentTime を巻き戻す。これが「最後の音が被って聞こえる」の正体。
    // 再生ヘッドの見た目は PLAYHEAD_UPDATE_SEC 刻みで十分なので、それ未満の変化は捨てる。
    const handleFrameUpdate = (e: { detail: { frame: number } }) => {
      const sec = e.detail.frame / SONG_AD_FPS;
      setCurrentSec((prev) => (Math.abs(sec - prev) >= PLAYHEAD_UPDATE_SEC ? sec : prev));
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    player.addEventListener('frameupdate', handleFrameUpdate);
    player.addEventListener('play', handlePlay);
    player.addEventListener('pause', handlePause);
    return () => {
      player.removeEventListener('frameupdate', handleFrameUpdate);
      player.removeEventListener('play', handlePlay);
      player.removeEventListener('pause', handlePause);
    };
    // playerVersion: attachPlayerRefがインスタンスの着脱ごとに更新するため、
    // 再マウント後も新インスタンスへ購読し直す（旧インスタンスにリスナーが残るのを防ぐ）
  }, [playerVersion]);

  const seekToSec = useCallback((sec: number) => {
    const frame = Math.round(Math.max(0, Math.min(totalDuration, sec)) * SONG_AD_FPS);
    playerRef.current?.seekTo(frame);
    setCurrentSec(frame / SONG_AD_FPS);
  }, [totalDuration]);

  /* ---------- 自動保存（2秒debounce） ---------- */

  useEffect(() => {
    if (!isDirtyRef.current) return undefined;
    setSaveStatus('pending');
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      setSaveStatus('saving');
      const timeline: SongAdTimeline = {
        ...baseTimeline,
        captions: history.present,
        updated_client_at: new Date().toISOString(),
      };
      onSaveTimeline(timeline)
        .then(() => setSaveStatus('saved'))
        .catch(() => setSaveStatus('error'));
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.present]);

  useEffect(() => () => {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    if (textCommitTimerRef.current) clearTimeout(textCommitTimerRef.current);
  }, []);

  /* ---------- 履歴コミット/プレビューのヘルパー ---------- */

  const commit = useCallback((next: CaptionSegment[]) => {
    isDirtyRef.current = true;
    dispatchHistory({ type: 'commit', captions: next });
    setLiveCaptions(null);
  }, []);

  const previewLive = useCallback((next: CaptionSegment[]) => {
    setLiveCaptions(next);
  }, []);

  const scheduleTextCommit = useCallback((next: CaptionSegment[]) => {
    previewLive(next);
    if (textCommitTimerRef.current) clearTimeout(textCommitTimerRef.current);
    textCommitTimerRef.current = setTimeout(() => commit(next), TEXT_COMMIT_DEBOUNCE_MS);
  }, [commit, previewLive]);

  const flushTextCommit = useCallback(() => {
    if (textCommitTimerRef.current) {
      clearTimeout(textCommitTimerRef.current);
      textCommitTimerRef.current = null;
      if (liveCaptions) commit(liveCaptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveCaptions, commit]);

  const handleUndo = useCallback(() => {
    flushTextCommit();
    isDirtyRef.current = true;
    dispatchHistory({ type: 'undo' });
  }, [flushTextCommit]);

  const handleRedo = useCallback(() => {
    flushTextCommit();
    isDirtyRef.current = true;
    dispatchHistory({ type: 'redo' });
  }, [flushTextCommit]);

  const selectCaption = useCallback((id: string | null) => {
    flushTextCommit();
    setSelectedCaptionId(id);
  }, [flushTextCommit]);

  /* ---------- テロップ操作 ---------- */

  const selected = captions.find((c) => c.id === selectedCaptionId) ?? null;

  const updateSelected = useCallback((patch: Partial<CaptionSegment>) => {
    if (!selected) return;
    const next = captions.map((c) => (c.id === selected.id ? { ...c, ...patch } : c));
    commit(next);
  }, [captions, commit, selected]);

  const handleTextChange = useCallback((text: string) => {
    if (!selected) return;
    const next = captions.map((c) => (c.id === selected.id ? { ...c, text } : c));
    scheduleTextCommit(next);
  }, [captions, scheduleTextCommit, selected]);

  const handleAddCaption = useCallback(() => {
    const created = createCaptionAt(currentSec, totalDuration);
    commit([...captions, created]);
    setSelectedCaptionId(created.id);
  }, [captions, commit, currentSec, totalDuration]);

  const handleDeleteSelected = useCallback(() => {
    if (!selected) return;
    commit(captions.filter((c) => c.id !== selected.id));
    setSelectedCaptionId(null);
  }, [captions, commit, selected]);

  const handleSplitSelected = useCallback(() => {
    if (!selected) return;
    const cursor = textareaRef.current?.selectionStart ?? Math.floor(selected.text.length / 2);
    const result = splitCaptionAt(selected, cursor);
    if (!result) return;
    const [first, second] = result;
    const next = captions.flatMap((c) => (c.id === selected.id ? [first, second] : [c]));
    commit(next);
    setSelectedCaptionId(second.id);
  }, [captions, commit, selected]);

  /* ---------- ドラッグ（移動/端リサイズ） ---------- */

  const startCaptionDrag = useCallback(
    (caption: CaptionSegment, mode: 'move' | 'start' | 'end') => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      selectCaption(caption.id);
      const startX = e.clientX;
      const resultRef = { current: caption };

      const onMove = (ev: MouseEvent) => {
        const deltaSec = (ev.clientX - startX) / pxPerSec;
        let next: CaptionSegment;
        if (mode === 'move') {
          next = moveCaption(caption, deltaSec, totalDuration);
        } else if (mode === 'start') {
          next = resizeCaptionStart(caption, caption.start + deltaSec);
        } else {
          next = resizeCaptionEnd(caption, caption.end + deltaSec, totalDuration);
        }
        resultRef.current = next;
        previewLive(captions.map((c) => (c.id === caption.id ? next : c)));
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        commit(captions.map((c) => (c.id === caption.id ? resultRef.current : c)));
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [captions, commit, previewLive, pxPerSec, selectCaption, totalDuration]
  );

  /* ---------- ルーラーシーク ---------- */

  const handleRulerMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const seek = (clientX: number) => seekToSec((clientX - rect.left) / pxPerSec);
    seek(e.clientX);
    const onMove = (ev: MouseEvent) => seek(ev.clientX);
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [pxPerSec, seekToSec]);

  const timelineWidth = Math.max(1, totalDuration) * pxPerSec;
  const tickCount = Math.floor(totalDuration) + 1;

  const saveStatusLabel: Record<SaveStatus, string> = {
    idle: '',
    pending: '変更あり',
    saving: '保存中...',
    saved: '保存済み',
    error: '保存に失敗しました',
  };

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 260px)', minHeight: 520 }}>
      {/* ツールバー */}
      <div className="flex items-center gap-1 px-2 py-2 border-b border-warm-border bg-white shrink-0 flex-wrap">
        <button
          onClick={onBackToPreview}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 rounded-lg px-2 py-1.5 hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          プレビュー
        </button>
        <div className="w-px h-5 bg-warm-border mx-1" />
        <button
          onClick={handleUndo}
          disabled={history.past.length === 0}
          title="元に戻す"
          className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent transition-colors"
        >
          <Undo2 className="w-4 h-4" />
        </button>
        <button
          onClick={handleRedo}
          disabled={history.future.length === 0}
          title="やり直す"
          className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent transition-colors"
        >
          <Redo2 className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-warm-border mx-1" />
        <button
          onClick={() => setPxPerSec((v) => Math.max(MIN_PX_PER_SEC, v / 1.5))}
          title="ズームアウト"
          className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <ZoomOut className="w-4 h-4" />
        </button>
        <button
          onClick={() => setPxPerSec((v) => Math.min(MAX_PX_PER_SEC, v * 1.5))}
          title="ズームイン"
          className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <ZoomIn className="w-4 h-4" />
        </button>
        <button
          onClick={() => {
            const width = timelineScrollRef.current?.clientWidth ?? 800;
            setPxPerSec(Math.max(MIN_PX_PER_SEC, Math.min(MAX_PX_PER_SEC, Math.floor((width - 20) / Math.max(1, totalDuration)))));
          }}
          title="全体表示"
          className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <Scan className="w-4 h-4" />
        </button>

        <div className="flex-1" />

        {saveStatus !== 'idle' && (
          <span
            className={`text-xs flex items-center gap-1 mr-2 ${
              saveStatus === 'error' ? 'text-destructive' : saveStatus === 'saved' ? 'text-green-600' : 'text-gray-400'
            }`}
          >
            {saveStatus === 'saving' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {saveStatusLabel[saveStatus]}
          </span>
        )}

        <TaskyButton size="sm" onClick={onProceedToExport}>
          <Download className="w-3.5 h-3.5" />
          書き出す
        </TaskyButton>
      </div>

      {/* メイン3ペイン */}
      <div className="flex-1 flex min-h-0">
        {/* 左：テロップ一覧 */}
        <div className="w-56 border-r border-warm-border flex flex-col shrink-0 bg-white">
          <div className="px-3 py-2 text-xs font-semibold text-gray-500 border-b border-warm-border flex items-center justify-between shrink-0">
            <span className="flex items-center gap-1">
              <Subtitles className="w-3.5 h-3.5" />
              テロップ（{captions.length}）
            </span>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {[...captions].sort((a, b) => a.start - b.start).map((c) => (
              <button
                key={c.id}
                data-testid={`caption-list-item-${c.id}`}
                onClick={() => selectCaption(c.id)}
                className={`w-full text-left p-2 rounded-lg mb-1 border ${
                  selectedCaptionId === c.id ? 'border-primary bg-primary/5' : 'border-transparent hover:bg-gray-50'
                }`}
              >
                <p className="text-xs text-gray-700 truncate">{c.text}</p>
                <p className="text-[10px] text-gray-400 tabular-nums">
                  {formatSec(c.start)} – {formatSec(c.end)}
                </p>
              </button>
            ))}
            {captions.length === 0 && <p className="text-xs text-gray-400 px-1 py-2">テロップがありません</p>}
          </div>
          <div className="p-2 border-t border-warm-border shrink-0">
            <TaskyButton variant="outline" size="sm" onClick={handleAddCaption} className="w-full">
              <Plus className="w-3.5 h-3.5" />
              テロップを追加
            </TaskyButton>
          </div>
        </div>

        {/* 中央：ミニプレビュー */}
        <div className="flex-1 min-w-0 bg-warm-surface flex items-center justify-center p-3">
          <div
            className="relative bg-black rounded-xl overflow-hidden shadow"
            style={{
              aspectRatio: `${dimensions.width} / ${dimensions.height}`,
              // v7: 旧実装は width を一切指定せず maxWidth（220px/340px固定）だけだったため、
              // 親のflex-column（align-items:center）でこの要素のcross-axisサイズが
              // fit-content計算になり、子のPlayerがwidth:100%指定（＝自身の親のサイズに依存＝算出不可）
              // しか持たないことでfit-content結果が0に収束 → aspect-ratioで高さも0になり、
              // プレビューが実質「非表示（0×0px）」になっていた（実機bounding rectで確認済み）。
              // heightを基準に幅をaspect-ratioから自動算出し、maxWidthは可用幅いっぱいまで許容する
              // ことで、縦方向に入る最大サイズまで大きく表示する（ユーザー要望「プレビューを大きく」）。
              height: '100%',
              maxHeight: '100%',
              width: 'auto',
              maxWidth: '100%',
            }}
          >
            <Player
              ref={attachPlayerRef}
              // durationInFrames/aspect_ratio（compositionWidth/Height由来）はRemotion Playerが
              // マウント時にしか読まない値なので、変更時のみkeyを変えて再マウントする。
              // compositionScenes.length はここに含めない
              // （キャプション編集など再マウント不要な更新でPlayerが作り直されるのを防ぐ）
              key={`${project.aspect_ratio}-${durationInFrames}`}
              component={SongAdComposition}
              inputProps={{
                scenes: stableMedia.scenes,
                captions,
                audioUrl: stableMedia.audioUrl,
                cta: stableMedia.cta,
              }}
              durationInFrames={durationInFrames}
              fps={SONG_AD_FPS}
              compositionWidth={dimensions.width}
              compositionHeight={dimensions.height}
              style={{ width: '100%', height: '100%' }}
              // v8.1: Remotion は「あるべきフレーム位置」と <Audio> の実再生位置の差がこの値を
              // 超えると currentTime を書き換えて同期し直す。巻き戻す方向に働くと直前の音が
              // もう一度鳴り「深淵よ→深淵よ淵よ」のように聞こえる。編集画面は3ペイン＋タイムラインで
              // 描画が重くクロックが遅れやすいので、既定 0.45 秒では頻繁に発動してしまう。
              // 許容を広げて同期し直す回数自体を減らす（PLAYHEAD_UPDATE_SEC の間引きと併用）
              acceptableTimeShiftInSeconds={1.5}
              // v7: 旧実装は controls（Remotion組み込みの再生バー）も有効にしていたが、箱が0×0で
              // 隠れていて誰も気づいていなかった。今回サイズを直したことで表示されるようになったところ、
              // このコンポーネント自前の再生ボタン/タイムスタンプ（下のオーバーレイ）およびタイムライン
              // ルーラー（画面下部・handleRulerMouseDown）とシーク機能が完全に重複し、二重の操作バーが
              // 映像に被る見た目になっていたため無効化した。シーク手段は既存のタイムラインで足りている
              // （既存の編集操作は変更していない）
              // v5 FB9 k: @remotion/player の既定 moveToBeginningWhenEnded=true により、
              // 最後まで再生すると（ループしない設定でも）内部フレームが自動的に0へ戻される
              // （node_modules/@remotion/player/dist/cjs/use-playback.js の再生ループ参照。
              // 実機ステージング(v4)で ended → audio.currentTime が61.39→0へseekされる実イベントを
              // 直接観測して確定）。「途中で繰り返される」の実体はこれ＝再生完了のたびに
              // 先頭へ巻き戻る挙動。detail editorでは巻き戻さず最終フレームで止める
              moveToBeginningWhenEnded={false}
            />
            {/* v7: 再生ボタン/タイムスタンプは映像下部にオーバーレイ表示にし、
                プレビューの縦スペースを専有しないようにする（別行で確保していた分だけ映像を拡大できる）。
                外枠はpointer-events-noneにして映像自体へのクリック（Remotionのクリック再生等）を妨げず、
                ボタン自体だけpointer-events-autoで受け付ける。
                EditorStep.test.tsx は <button> 内の svg.lucide-play/pause を探すだけなので
                DOM位置の変更は非破壊 */}
            <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-2 py-2 bg-gradient-to-t from-black/70 to-transparent pointer-events-none">
              <button
                onClick={() => (isPlaying ? playerRef.current?.pause() : playerRef.current?.play())}
                className="w-8 h-8 rounded-full bg-white/90 text-dark flex items-center justify-center hover:bg-white transition-colors pointer-events-auto"
              >
                {isPlaying ? <Pause className="w-3.5 h-3.5 fill-current" /> : <Play className="w-3.5 h-3.5 fill-current" />}
              </button>
              <span
                className="text-[11px] text-white tabular-nums"
                style={{ textShadow: '0 1px 2px rgba(0,0,0,.7)' }}
              >
                {formatSec(currentSec)} / {formatSec(totalDuration)}
              </span>
            </div>
          </div>
        </div>

        {/* 右：インスペクタ */}
        <div className="w-72 border-l border-warm-border overflow-y-auto shrink-0 bg-white p-4">
          {!selected && (
            <p className="text-xs text-gray-400 text-center mt-8 leading-relaxed">
              タイムラインや左のリストから
              <br />
              テロップを選択すると
              <br />
              ここで編集できます
            </p>
          )}
          {selected && (
            <div>
              <p className="text-sm font-semibold text-dark mb-3">テロップ</p>
              <label className="block text-[11px] font-medium text-gray-500 mb-1">文言（改行できます）</label>
              <TaskyTextarea
                ref={textareaRef}
                value={selected.text}
                onChange={(e) => handleTextChange(e.target.value)}
                onBlur={flushTextCommit}
                rows={3}
                className="mb-3 text-sm"
              />
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1">表示開始（秒）</label>
                  <input
                    type="number"
                    step={0.1}
                    min={0}
                    max={totalDuration}
                    value={selected.start}
                    onChange={(e) => updateSelected(resizeCaptionStart(selected, Number(e.target.value)))}
                    className="bg-warm-surface rounded-lg border-none p-2 text-sm w-full"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1">表示終了（秒）</label>
                  <input
                    type="number"
                    step={0.1}
                    min={0}
                    max={totalDuration}
                    value={selected.end}
                    onChange={(e) => updateSelected(resizeCaptionEnd(selected, Number(e.target.value), totalDuration))}
                    className="bg-warm-surface rounded-lg border-none p-2 text-sm w-full"
                  />
                </div>
              </div>

              <label className="block text-[11px] font-medium text-gray-500 mb-1">表示位置</label>
              <div className="inline-flex w-full rounded-lg overflow-hidden border border-warm-border mb-3 text-xs">
                {CAPTION_POSITION_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => updateSelected({ position: opt.key as TelopPosition })}
                    className={`flex-1 py-1.5 transition-colors ${
                      selected.position === opt.key ? 'bg-primary text-white font-medium' : 'text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <label className="block text-[11px] font-medium text-gray-500 mb-1">サイズ</label>
              <div className="inline-flex w-full rounded-lg overflow-hidden border border-warm-border mb-3 text-xs">
                {CAPTION_SIZE_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => updateSelected({ size: opt.key as TelopSize })}
                    className={`flex-1 py-1.5 transition-colors ${
                      selected.size === opt.key ? 'bg-primary text-white font-medium' : 'text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <label className="block text-[11px] font-medium text-gray-500 mb-1.5">スタイル</label>
              <div className="grid grid-cols-2 gap-2 mb-4">
                {CAPTION_STYLE_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => updateSelected({ style: opt.key as CaptionStyle })}
                    className={`rounded-lg p-2 flex flex-col items-center gap-1 border transition-colors ${
                      selected.style === opt.key ? 'border-primary bg-primary/5' : 'border-warm-border hover:border-gray-300'
                    }`}
                  >
                    <span className={`w-full h-8 rounded flex items-center justify-center text-xs ${CAPTION_BLOCK_CLASS[opt.key]}`}>
                      Aa あ
                    </span>
                    <span className="text-[10px] text-gray-500">{opt.label}</span>
                  </button>
                ))}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleSplitSelected}
                  className="flex-1 flex items-center justify-center gap-1 text-xs text-gray-600 bg-warm-surface hover:bg-gray-100 rounded-lg py-2 transition-colors"
                  title="文言のカーソル位置で分割"
                >
                  <Scissors className="w-3.5 h-3.5" />
                  ここで分割
                </button>
                <button
                  onClick={handleDeleteSelected}
                  className="flex items-center justify-center gap-1 text-xs text-destructive bg-red-50 hover:bg-red-100 rounded-lg px-3 py-2 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="text-[10px] text-gray-400 mt-2 leading-relaxed">
                分割位置は文言内のカーソル位置。表示時間は文字数で自動配分されます。
              </p>
            </div>
          )}
        </div>
      </div>

      {/* タイムライン（3トラック） */}
      <div className="border-t border-warm-border shrink-0 bg-white flex" style={{ height: 176 }}>
        <div className="w-14 shrink-0 border-r border-warm-border text-[10px] text-gray-400 flex flex-col">
          <div style={{ height: 24 }} />
          <div className="flex items-center px-2" style={{ height: 44 }}>
            <Video className="w-3 h-3 mr-1" />
            映像
          </div>
          <div className="flex items-center px-2" style={{ height: 36 }}>
            <Subtitles className="w-3 h-3 mr-1" />
            テロップ
          </div>
          <div className="flex items-center px-2" style={{ height: 36 }} title="MVPでは楽曲のカット編集はできません">
            <Music className="w-3 h-3 mr-1" />
            楽曲
            <Lock className="w-2.5 h-2.5 ml-1" />
          </div>
        </div>

        <div ref={timelineScrollRef} className="flex-1 overflow-x-auto overflow-y-hidden relative">
          <div className="relative" style={{ width: timelineWidth + 40 }}>
            {/* ルーラー */}
            <div
              onMouseDown={handleRulerMouseDown}
              className="relative cursor-pointer border-b border-warm-border"
              style={{ height: 24 }}
            >
              {Array.from({ length: tickCount }, (_, s) => (
                <React.Fragment key={s}>
                  <span
                    className={`absolute bottom-0 border-l ${s % 5 === 0 ? 'border-gray-400 h-3' : 'border-gray-200 h-1.5'}`}
                    style={{ left: s * pxPerSec }}
                  />
                  {s % 5 === 0 && (
                    <span className="absolute top-0.5 text-[9px] text-gray-400 tabular-nums" style={{ left: s * pxPerSec + 3 }}>
                      {s}s
                    </span>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* 映像トラック（参照表示のみ。境界ドラッグ不可）。
                renderScenes はCTAカード分だけ末尾を縮めた枠（deriveCtaCard）。書き出しと一致させる */}
            <div className="relative border-b border-warm-border/60" style={{ height: 44 }}>
              {renderScenes.map((scene, i) => (
                <button
                  key={scene.id}
                  onClick={() => seekToSec(scene.startSec + 0.05)}
                  className="absolute top-1 bottom-1 rounded-md overflow-hidden border border-warm-border bg-gray-100"
                  style={{
                    left: scene.startSec * pxPerSec,
                    width: Math.max(16, (scene.endSec - scene.startSec) * pxPerSec - 2),
                    backgroundImage: scene.imageUrl ? `url(${scene.imageUrl})` : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                  title={`シーン ${i + 1}`}
                >
                  <span className="absolute top-0.5 left-1 text-[9px] font-bold text-white" style={{ textShadow: '0 1px 3px rgba(0,0,0,.6)' }}>
                    {i + 1}
                    {scene.motionMode === 'clip' ? ' ▶' : ''}
                  </span>
                </button>
              ))}
              {cta && (
                <button
                  onClick={() => seekToSec(cta.start + 0.05)}
                  className="absolute top-1 bottom-1 rounded-md overflow-hidden border border-primary bg-primary/10 flex items-center justify-center"
                  style={{ left: cta.start * pxPerSec, width: Math.max(16, (cta.end - cta.start) * pxPerSec - 2) }}
                  title={`CTA: ${cta.text}`}
                >
                  <span className="text-[9px] font-bold text-primary truncate px-1">CTA</span>
                </button>
              )}
            </div>

            {/* テロップトラック */}
            <div className="relative border-b border-warm-border/60" style={{ height: 36 }}>
              {captions.map((c) => (
                <div
                  key={c.id}
                  onMouseDown={startCaptionDrag(c, 'move')}
                  className={`absolute top-1 bottom-1 rounded-lg overflow-hidden cursor-pointer select-none border flex items-center px-1.5 ${
                    CAPTION_BLOCK_CLASS[c.style]
                  } ${selectedCaptionId === c.id ? 'ring-2 ring-primary' : ''}`}
                  style={{ left: c.start * pxPerSec, width: Math.max(14, (c.end - c.start) * pxPerSec - 2) }}
                >
                  <span className="text-[9px] truncate pointer-events-none">{c.text}</span>
                  <span
                    onMouseDown={startCaptionDrag(c, 'start')}
                    className="absolute top-0 bottom-0 w-2 cursor-ew-resize"
                    style={{ left: -2 }}
                  />
                  <span
                    onMouseDown={startCaptionDrag(c, 'end')}
                    className="absolute top-0 bottom-0 w-2 cursor-ew-resize"
                    style={{ right: -2 }}
                  />
                </div>
              ))}
            </div>

            {/* 楽曲トラック（固定・編集不可） */}
            <div className="relative" style={{ height: 36 }}>
              <div
                className="absolute rounded-lg bg-indigo-50 border border-indigo-200 flex items-center px-2"
                style={{ left: 0, width: timelineWidth, top: 4, bottom: 4 }}
              >
                <span className="text-[10px] text-primary truncate">{project.title}</span>
              </div>
            </div>

            {/* 再生ヘッド */}
            <div className="absolute top-0 bottom-0 z-20 pointer-events-none" style={{ left: currentSec * pxPerSec }}>
              <div className="w-[2px] h-full bg-primary mx-auto" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditorStep;
